import React, { useEffect, useRef, useState } from 'react';
import TextElement from '../../elements/TextElement';
import ImageElement from '../../elements/ImageElement';
import ShapeElement from '../../elements/ShapeElement';

export default function ElementRenderer({
  element,
  isSelected,
  onSelect,
  onChange,
  onDelete,
  onDuplicate,
  onZIndexChange,
  containerRef,
  ensureOverlay,
  saveElements,
  generatingElementId,
}) {
  // Create a ref to find the element in the DOM
  const rendererWrapperRef = useRef(null);

  // Track if we've updated the z-index locally
  const zIndexUpdated = useRef(false);
  
  // Track if we're currently applying a z-index update to avoid flickering
  const [isUpdatingZIndex, setIsUpdatingZIndex] = useState(false);
  const prevZIndex = useRef(element.zIndex);

  // Add a local state to track the actual z-index being displayed
  const [displayZIndex, setDisplayZIndex] = useState(element.zIndex || 0);
  
  useEffect(() => {
    if (element.type === 'image') {
      console.log('[ElementRenderer] Rendering image:', {
        id: element.id,
        type: element.type,
        imagePrompts: element.imagePrompts,
        promptCount: element.imagePrompts?.length || 0,
        imagePrompt: element.imagePrompt,
        hasPrompt: !!(element.imagePrompts && element.imagePrompts.length > 0) || !!element.imagePrompt,
        spreadId: element.spreadId,
        visualStyle: element.visualStyle,
        orientation: element.orientation,
        orientationType: typeof element.orientation,
      });
      console.log('[ElementRenderer] Full element object:', element);
    }
  }, [element]);

  // Effect to track element prop changes for debugging
  useEffect(() => {
    console.log(`[ZINDEX DEBUG] ElementRenderer element prop updated:`, {
      id: element.id,
      zIndex: element.zIndex,
      zIndexType: typeof element.zIndex
    });
  }, [element]);

  // Update display z-index whenever the element's z-index changes
  useEffect(() => {
    const actualZIndex = element.zIndex || 0;
    console.log('[DEBUG Z-INDEX] Element props changed in ElementRenderer:', {
      id: element.id,
      type: element.type,
      newZIndex: actualZIndex,
      oldDisplayZIndex: displayZIndex,
      computedZIndex: rendererWrapperRef.current ? 
        window.getComputedStyle(rendererWrapperRef.current).zIndex : 'unknown'
    });
    setDisplayZIndex(actualZIndex);
  }, [element.zIndex, element.id]);

  // Add an effect to check if the DOM element has a different z-index than what we display
  useEffect(() => {
    if (rendererWrapperRef.current) {
      const computedZIndex = window.getComputedStyle(rendererWrapperRef.current).zIndex;
      const styleZIndex = rendererWrapperRef.current.style.zIndex;
      
      console.log('[VERIFY Z-INDEX] DOM vs State z-index check:', {
        id: element.id,
        type: element.type,
        propZIndex: element.zIndex,
        displayZIndex,
        styleZIndex,
        computedZIndex,
        mismatch: String(displayZIndex) !== computedZIndex && computedZIndex !== 'auto'
      });
    }
  }, [displayZIndex, element.id, element.zIndex]);

  // Effect to force update the UI when z-index changes
  useEffect(() => {
    // Make sure we always display the most up-to-date z-index from props
    if (rendererWrapperRef.current) {
      const safeZIndex = typeof element.zIndex === 'number' 
        ? element.zIndex 
        : parseInt(element.zIndex || '0');
      
      console.log(`[ZINDEX DEBUG] ElementRenderer syncing z-index from props:`, {
        id: element.id,
        propsZIndex: element.zIndex,
        propsZIndexType: typeof element.zIndex,
        safeZIndex,
        previousZIndex: prevZIndex.current
      });
      
      // Apply z-index directly to DOM
      rendererWrapperRef.current.style.zIndex = String(safeZIndex);
      console.log(`[ZINDEX DEBUG] After DOM update:`, {
        id: element.id,
        domStyleZIndex: rendererWrapperRef.current.style.zIndex,
        computedZIndex: window.getComputedStyle(rendererWrapperRef.current).zIndex
      });
      
      // Flash the indicator when z-index changes
      if (prevZIndex.current !== safeZIndex) {
        console.log(`[ZINDEX DEBUG] Z-index changed, flashing indicator:`, {
          id: element.id,
          from: prevZIndex.current,
          to: safeZIndex
        });
        
        setIsUpdatingZIndex(true);
        setTimeout(() => setIsUpdatingZIndex(false), 300);
        prevZIndex.current = safeZIndex;
      }
    }
  }, [element.zIndex, element.id]);

  // Set up the DOM element styles when ref is available or element changes
  useEffect(() => {
    if (rendererWrapperRef.current && containerRef?.current) {
      // Apply position and dimensions with gutter offset
      const el = rendererWrapperRef.current;
      const container = containerRef.current;
      const containerWidth = container.offsetWidth;
      const centerX = containerWidth / 2;
      
      // For image and text elements, don't apply any dynamic gutter shift at
      // the wrapper level. The inner BaseElement uses the real page
      // coordinates, and additional wrapper movement can break hit-testing or
      // feel visually confusing while dragging.
      if (element.type === 'image' || element.type === 'text') {
        el.style.position = 'absolute';
        el.style.top = '0px';
        el.style.left = '0px';
        el.style.width = `${element.size.width}px`;
        el.style.height = `${element.size.height}px`;
        return;
      }

      // Calculate gutter offset (60px padding from center)
      const gutterPadding = 60;
      // Start from the element's logical position
      const logicalX = element.position?.x ?? 0;
      let adjustedX = logicalX;
      
      // Only apply gutter offset for spread view (not landscape)
      const isSpreadView = containerWidth > container.offsetHeight;
      if (isSpreadView) {
        // Determine which side of the spread this element is on
        if (logicalX < centerX) {
          // Left page - push content away from center (towards left edge)
          if (element.type !== 'text') { // Don't apply gutter logic to text elements
            const distanceFromCenter = centerX - logicalX;
            if (distanceFromCenter < gutterPadding) {
              adjustedX = Math.max(-50, logicalX - (gutterPadding - distanceFromCenter));
            }
          }
        } else {
          // Right page - push content away from center (towards right edge)  
          if (element.type !== 'text') { // Don't apply gutter logic to text elements
            const distanceFromCenter = logicalX - centerX;
            if (distanceFromCenter < gutterPadding) {
              adjustedX = Math.min(
                containerWidth - element.size.width - 20,
                logicalX + (gutterPadding - distanceFromCenter)
              );
            }
          }
        }
      }

      // The inner BaseElement also positions itself using element.position (page
      // coordinates). To avoid double-applying the position and causing a jump,
      // the wrapper only applies the *gutter offset* (difference between
      // adjusted and logical X) while keeping Y at 0.
      const gutterOffsetX = adjustedX - logicalX;

      el.style.position = 'absolute';
      el.style.top = '0px';
      el.style.left = `${gutterOffsetX}px`;
      el.style.width = `${element.size.width}px`;
      el.style.height = `${element.size.height}px`;
    }
  }, [element.id, element.position, element.size, containerRef]);

  if (!element?.id) {
    console.error('[ElementRenderer] Invalid element:', element);
    return null;
  }

  const handleZIndexChange = (elementId, newZIndex) => {
    console.log('[DIAGNOSE] ElementRenderer.handleZIndexChange called:', {
      elementId, 
      newZIndex,
      propElementId: element.id,
      propZIndex: element.zIndex,
      onZIndexChangeExists: !!onZIndexChange,
      onZIndexChangeType: typeof onZIndexChange
    });
    
    // Make sure we're dealing with a number
    const safeNewZIndex = typeof newZIndex === 'number' ? newZIndex : parseInt(newZIndex || '0', 10);
    
    // 1. Update the DOM directly for immediate visual feedback
    if (rendererWrapperRef.current) {
      rendererWrapperRef.current.style.zIndex = String(safeNewZIndex);
      
      console.log('[DIAGNOSE] ElementRenderer DOM update:', {
        styleZIndex: rendererWrapperRef.current.style.zIndex,
        computedZIndex: window.getComputedStyle(rendererWrapperRef.current).zIndex
      });
    }
    
    // 2. Update local display state to show the correct number in the indicator
    setDisplayZIndex(safeNewZIndex);
    
    // 3. Highlight the z-index indicator for visual feedback
    setIsUpdatingZIndex(true);
    setTimeout(() => {
      setIsUpdatingZIndex(false);
    }, 300);
    
    // 4. Propagate the change up to parent components
    if (onZIndexChange) {
      console.log('[DIAGNOSE] ElementRenderer passing to parent:', {
        functionSignature: onZIndexChange.toString().slice(0, 100)
      });
      onZIndexChange(element.id, safeNewZIndex);
    }
  };

  const commonProps = {
    id: element.id,
    // Use the element's actual position so drag calculations start from the
    // correct page coordinates. The wrapper now only applies gutter offset.
    position: element.position || { x: 0, y: 0 },
    size: element.size,
    style: element.style,
    zIndex: element.zIndex || 1,
    isSelected,
    pagePosition: element.pagePosition,
    onSelect,
    onChange,
    onDelete,
    onDuplicate,
    onZIndexChange: handleZIndexChange,
    containerRef,
  };

  const elementClasses = `
    ${isSelected ? 'border border-blue-500' : ''}
    ${isUpdatingZIndex ? 'transition-z-index' : ''}
  `.trim();
  
  switch (element.type) {
    case 'text':
      return (
        <div
          ref={rendererWrapperRef}
          className="element-wrapper text-element-wrapper"
          style={{ display: 'contents' }}
        >
          <TextElement
            key={element.id}
            id={element.id}
            content={element.content}
            audio={element.audio}
            spreadId={element.spreadId}
            position={commonProps.position}
            size={element.size}
            style={element.style}
            zIndex={element.zIndex || 1}
            isSelected={isSelected}
            onSelect={onSelect}
            onMove={(position) => onChange({ position })}
            onResize={(size) => onChange({ size })}
            onChange={onChange}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onZIndexChange={(newZIndex) => handleZIndexChange(element.id, newZIndex)}
            containerRef={containerRef}
          />
          {isSelected && (
            <div 
              className="absolute -top-6 right-0 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded-md opacity-90 z-index-indicator"
              style={{ 
                pointerEvents: 'none',
                transition: 'all 0.2s ease-in-out',
                transform: isUpdatingZIndex ? 'scale(1.2)' : 'scale(1)',
                backgroundColor: isUpdatingZIndex ? '#4F46E5' : '#1F2937',
                fontWeight: isUpdatingZIndex ? 'bold' : 'normal',
                boxShadow: isUpdatingZIndex ? '0 0 8px rgba(79, 70, 229, 0.6)' : 'none'
              }}
            >
              z-index: {displayZIndex}
            </div>
          )}
        </div>
      );
    case 'image':
      return (
        <div 
          ref={rendererWrapperRef}
          // For images, avoid applying the generic selection border on the
          // outer wrapper, since the inner BaseElement already shows a
          // selection outline. Using elementClasses here puts a blue box at
          // the top-left (wrapper origin) which is visually confusing.
          className="element-wrapper image-element-wrapper"
          style={{ display: 'contents' }}
        >
          <ImageElement
            key={element.id}
            id={element.id}
            src={element.src}
            position={commonProps.position}
            size={element.size}
            style={element.style}
            zIndex={element.zIndex || 1}
            imagePrompts={element.imagePrompts}
            imagePrompt={element.imagePrompt}
            images={element.images}
            spreadId={element.spreadId}
            orientation={element.orientation}
            hasBorder={element.hasBorder !== undefined ? element.hasBorder : true} // Pass the hasBorder property
            borderColor={element.borderColor}
            borderWidth={element.borderWidth}
            borderStyle={element.borderStyle}
            borderRadius={element.borderRadius}
            borderOpacity={element.borderOpacity}
            isSelected={isSelected}
            onSelect={onSelect}
            onMove={(position) => onChange({ position })}
            onResize={(size) => onChange({ size })}
            onUpdate={(updates) => onChange(updates)}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onZIndexChange={(newZIndex) => handleZIndexChange(element.id, newZIndex)}
            containerRef={containerRef}
            visualStyle={element.visualStyle}
            imageGenerationStatus={element.imageGenerationStatus}
            imageUpdateJobId={element.imageUpdateJobId}
            lastGenerationFailed={element.lastGenerationFailed}
            lastGenerationError={element.lastGenerationError}
            isGenerating={generatingElementId === element.id}
            ensureOverlay={ensureOverlay}
            saveElements={saveElements}
          />
          {isSelected && (
            <div 
              className="absolute -top-6 right-0 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded-md opacity-90 z-index-indicator"
              style={{ 
                pointerEvents: 'none',
                transition: 'all 0.2s ease-in-out',
                transform: isUpdatingZIndex ? 'scale(1.2)' : 'scale(1)',
                backgroundColor: isUpdatingZIndex ? '#4F46E5' : '#1F2937',
                fontWeight: isUpdatingZIndex ? 'bold' : 'normal',
                boxShadow: isUpdatingZIndex ? '0 0 8px rgba(79, 70, 229, 0.6)' : 'none'
              }}
            >
              z-index: {displayZIndex}
            </div>
          )}
        </div>
      );
    case 'shape':
      return (
        <div 
          ref={rendererWrapperRef}
          // Avoid showing an extra selection border on the wrapper; the inner
          // BaseElement already renders the blue selection outline.
          className="element-wrapper shape-element-wrapper"
          style={{ display: 'contents' }}
        >
          <ShapeElement
            key={element.id}
            id={element.id}
            shape={element.shape}
            position={commonProps.position}
            size={element.size}
            style={element.style || {}} // Ensure style is never undefined
            zIndex={element.zIndex || 1}
            isSelected={isSelected}
            onSelect={onSelect}
            onMove={(position) => onChange({ position })}
            onResize={(size) => onChange({ size })}
            onUpdate={(updates) => onChange(updates)}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onZIndexChange={(newZIndex) => handleZIndexChange(element.id, newZIndex)}
            containerRef={containerRef}
          />
          {isSelected && (
            <div 
              className="absolute -top-6 right-0 bg-gray-800 text-white text-xs px-1.5 py-0.5 rounded-md opacity-90 z-index-indicator"
              style={{ 
                pointerEvents: 'none',
                transition: 'all 0.2s ease-in-out',
                transform: isUpdatingZIndex ? 'scale(1.2)' : 'scale(1)',
                backgroundColor: isUpdatingZIndex ? '#4F46E5' : '#1F2937',
                fontWeight: isUpdatingZIndex ? 'bold' : 'normal',
                boxShadow: isUpdatingZIndex ? '0 0 8px rgba(79, 70, 229, 0.6)' : 'none'
              }}
            >
              z-index: {displayZIndex}
            </div>
          )}
        </div>
      );
    default:
      console.error(
        '[ElementRenderer] Unknown element type:',
        element.type,
        element
      );
      return null;
  }
}
